import { lazy, Suspense } from 'react'

import { Navigate, Route, Routes } from 'react-router'
import { Outlet } from 'react-router-dom'

import { PostTypeContext } from '@tribeplatform/gql-client/types'
import { Container } from '@tribeplatform/react-ui-kit/Layout'

import type { AppInitialProps } from '../@types/index.js'
import { Analytics } from './components/Analytics/Analytics.js'
import { RouterErrorBoundary } from './components/Error/RouterErrorBoundary.js'
import { SlateLayout } from './components/Layout/layout.enum.js'
import { PageLayout } from './components/Layout/PageLayout.js'
import { SettingsLayout } from './components/Settings/SettingsLayout.js'
import { SlateApp } from './SlateApp.js'
import { MESSAGING_POST_TYPE_SLUG } from './utils/messaging.utils.js'

const AppPage = lazy(() =>
  import('./components/Admin/Apps/AppPage.js').then(m => ({
    default: m.AppPage,
  })),
)
const Apps = lazy(() =>
  import('./components/Admin/Apps/index.js').then(m => ({
    default: m.Apps,
  })),
)
const GettingStarted = lazy(() =>
  import('./components/Admin/Dashboard/GettingStarted/index.js').then(m => ({
    default: m.GettingStarted,
  })),
)
const WhatsNew = lazy(() =>
  import('./components/Admin/Dashboard/WhatsNew.js').then(m => ({
    default: m.WhatsNew,
  })),
)
const Preview = lazy(() =>
  import('./components/Admin/Editor/Preview/index.js').then(m => ({
    default: m.Preview,
  })),
)
const EmailLogs = lazy(() =>
  import('./components/Admin/EmailLogs/index.js').then(m => ({
    default: m.EmailLogs,
  })),
)
const Admin = lazy(() =>
  import('./components/Admin/index.js').then(m => ({
    default: m.Admin,
  })),
)
const MemberBadges = lazy(() =>
  import('./components/Admin/MemberBadges/index.js').then(m => ({
    default: m.MemberBadges,
  })),
)
const MemberFields = lazy(() =>
  import('./components/Admin/MemberFields/index.js').then(m => ({
    default: m.MemberFields,
  })),
)
const Staff = lazy(() =>
  import('./components/Admin/Members/Staff.js').then(m => ({
    default: m.Staff,
  })),
)
const Members = lazy(() =>
  import('./components/Admin/Members/index.js').then(m => ({
    default: m.Members,
  })),
)
const Invitations = lazy(() =>
  import('./components/Admin/Members/Invitations.js').then(m => ({
    default: m.Invitations,
  })),
)
const ModerationSettings = lazy(() =>
  import('./components/Admin/ModerationSettings.js').then(m => ({
    default: m.ModerationSettings,
  })),
)
const Onboarding = lazy(() =>
  import('./components/Admin/Onboarding/index.js').then(m => ({
    default: m.Onboarding,
  })),
)
const Posts = lazy(() =>
  import('./components/Admin/Posts/index.js').then(m => ({
    default: m.Posts,
  })),
)
const PostTypeFields = lazy(() =>
  import('./components/Admin/PostTypes/PostTypeFields.js').then(m => ({
    default: m.PostTypeFields,
  })),
)
const Security = lazy(() =>
  import('./components/Admin/Security/index.js').then(m => ({
    default: m.Security,
  })),
)
const Authentication = lazy(() =>
  import('./components/Admin/Settings/Authentication/index.js').then(m => ({
    default: m.Authentication,
  })),
)
const Settings = lazy(() =>
  import('./components/Admin/Settings/CommunitySettings/index.js').then(m => ({
    default: m.Settings,
  })),
)
const Domain = lazy(() =>
  import('./components/Admin/Settings/Domain/index.js').then(m => ({
    default: m.Domain,
  })),
)
const LocalizationSettings = lazy(() =>
  import('./components/Admin/Settings/Localization/index.js').then(m => ({
    default: m.LocalizationSettings,
  })),
)
const Notifications = lazy(() =>
  import('./components/Admin/Settings/Notifications/index.js').then(m => ({
    default: m.Notifications,
  })),
)
const SeoSettings = lazy(() =>
  import('./components/Admin/Settings/SeoSettings.js').then(m => ({
    default: m.SeoSettings,
  })),
)
const Spaces = lazy(() =>
  import('./components/Admin/Spaces/index.js').then(m => ({
    default: m.Spaces,
  })),
)
const Tags = lazy(() =>
  import('./components/Admin/Tags/index.js').then(m => ({
    default: m.Tags,
  })),
)

const SpaceRoutes = lazy(() =>
  import('./components/SpaceRoutes.component.js').then(m => ({
    default: m.SpaceRoutes,
  })),
)

const PostEmbed = lazy(() =>
  import('./embed/Post/index.js').then(m => ({
    default: m.PostEmbed,
  })),
)

const SpaceEmbed = lazy(() =>
  import('./embed/Space/index.js').then(m => ({
    default: m.SpaceEmbed,
  })),
)

const Unsubscribe = lazy(() =>
  import('./components/Settings/Unsubscribe.js').then(m => ({
    default: m.Unsubscribe,
  })),
)

const DownloadMembers = lazy(() =>
  import('./components/Admin/Members/DownloadMembers.js').then(m => ({
    default: m.DownloadMembers,
  })),
)

const MessagingSettings = lazy(() =>
  import('./components/Admin/Messaging/index.js').then(m => ({
    default: m.Messaging,
  })),
)

const Messaging = lazy(() =>
  import('./components/Messaging/index.js').then(m => ({
    default: m.Messaging,
  })),
)

const MessagingLayout = lazy(() =>
  import('./components/Messaging/MessagingLayout.js').then(m => ({
    default: m.MessagingLayout,
  })),
)

const Moderation = lazy(() =>
  import('./components/Moderation/index.js').then(m => ({
    default: m.Moderation,
  })),
)

const OAuth = lazy(() =>
  import('./components/OAuth/OAuth.js').then(m => ({ default: m.OAuth })),
)

const BillingPage = lazy(() =>
  import('./components/Admin/Billing/Details/BillingPage.js').then(m => ({
    default: m.BillingPage,
  })),
)
const PlansPage = lazy(() =>
  import('./components/Admin/Billing/Plans/PlansPage.js').then(m => ({
    default: m.PlansPage,
  })),
)
const Logout = lazy(() =>
  import('./components/Logout.js').then(m => ({
    default: m.Logout,
  })),
)

const MemberAccountSettings = lazy(() =>
  import('./components/Settings/AccountSettings.js').then(m => ({
    default: m.AccountSettings,
  })),
)
const MemberColorSchemeSettings = lazy(() =>
  import('./components/Settings/ColorSchemeSettings.js').then(m => ({
    default: m.ColorSchemeSettings,
  })),
)
const MemberConnectedApps = lazy(() =>
  import('./components/Settings/MemberConnectedApps/index.js').then(m => ({
    default: m.MemberConnectedApps,
  })),
)
const MemberNotificationSettings = lazy(() =>
  import('./components/Settings/NotificationSettings.js').then(m => ({
    default: m.NotificationSettings,
  })),
)
const MemberSecuritySettings = lazy(() =>
  import('./components/Settings/SecuritySettings.js').then(m => ({
    default: m.SecuritySettings,
  })),
)
const MemberMessagingSettings = lazy(() =>
  import('./components/Settings/MessagingSettings.js').then(m => ({
    default: m.MessagingSettings,
  })),
)

export const AppRoutes = ({ pageProps }: { pageProps: AppInitialProps }) => {
  const { network } = pageProps.authToken ?? {}

  return (
    <Routes>
      <Route
        path="/admin"
        element={<Navigate to="/manage/people/members" replace />}
      />
      <Route
        path="/manage/people/members/export/:id"
        element={
          <Suspense fallback={<></>}>
            <DownloadMembers />
          </Suspense>
        }
        errorElement={<RouterErrorBoundary />}
      />
      <Route
        path="/manage/*"
        element={
          <Suspense fallback={<></>}>
            <Admin>
              <Outlet />
            </Admin>
          </Suspense>
        }
        errorElement={<RouterErrorBoundary />}
      >
        <Route
          index
          element={<Navigate to="/manage/people/members" replace />}
        />
        <Route path="people/fields" element={<MemberFields />} />
        <Route path="people/badges" element={<MemberBadges />} />
        <Route
          path="content/post-types"
          element={
            <Navigate
              to={`/manage/content/post-types/${
                network?.postTypes?.filter(
                  postType =>
                    postType.slug !== MESSAGING_POST_TYPE_SLUG &&
                    postType.context === PostTypeContext.post,
                )?.[0]?.id
              }`}
              replace
            />
          }
        />

        <Route path="content/post-types/:id" element={<PostTypeFields />} />
        {[
          'customizer/post-types/:postTypeId',
          'customizer/post-types/:postTypeId/blocks',
          'customizer/spaces/:id',
          'customizer/spaces/:id/blocks',
          'customizer/colors',
          'customizer/navigation',
          'customizer/typography',
          'customizer/post-types',
          'customizer/spaces',
          'customizer',
          'appearance/*',
        ].map(path => (
          <Route key="preview" path={path} element={<Preview />} />
        ))}

        {/* With default padding */}
        <Route
          path="*"
          element={
            <Container
              size="full"
              padding={{
                vertical: 'md',
                horizontal: { size: 'md', disableOnMobile: true },
              }}
            >
              <Outlet />
            </Container>
          }
        >
          <Route path="dashboard/whats-new" element={<WhatsNew />} />
          <Route path="reports/email-logs" element={<EmailLogs />} />

          <Route path="people/members" element={<Members />} />
          <Route path="people/invitations" element={<Invitations />} />

          <Route
            path="people/admin"
            element={<Navigate to="/manage/people/staff" replace />}
          />
          <Route path="people/staff" element={<Staff />} />

          <Route path="content" element={<Posts />} />
          <Route path="content/posts" element={<Posts />} />
          <Route path="content/spaces" element={<Spaces />} />
          <Route path="content/tags" element={<Tags />} />

          <Route
            path="dashboard/getting-started"
            element={<GettingStarted />}
          />

          <Route path="settings/network" element={<Settings />} />
          <Route
            path="settings/localization"
            element={<LocalizationSettings />}
          />
          <Route path="settings/authentication" element={<Authentication />} />
          <Route path="settings/domain" element={<Domain />} />
          <Route path="settings/messaging" element={<MessagingSettings />} />
          <Route path="settings/notifications" element={<Notifications />} />
          <Route path="settings/seo" element={<SeoSettings />} />
          <Route path="settings/security" element={<Security />} />

          <Route path="apps" element={<Apps />} />
          <Route path="apps/:appSlug" element={<AppPage />} />

          <Route path="settings/moderation" element={<ModerationSettings />} />

          <Route path="settings/billing/plans" element={<PlansPage />} />
          <Route path="settings/billing/*" element={<BillingPage />} />

          <Route path="reports/*" element={<Analytics network={network} />} />

          <Route path="onboarding" element={<Onboarding />} />
        </Route>
      </Route>

      <Route
        path="/auth/logout"
        element={
          <Suspense fallback={<></>}>
            <Logout />
          </Suspense>
        }
        errorElement={<RouterErrorBoundary />}
      />
      <Route
        path="/embed/post/:id"
        element={
          <Suspense fallback={<></>}>
            <PostEmbed />
          </Suspense>
        }
        errorElement={<RouterErrorBoundary />}
      />
      <Route
        path="/embed/space/:slug"
        element={
          <Suspense fallback={<></>}>
            <SpaceEmbed />
          </Suspense>
        }
        errorElement={<RouterErrorBoundary />}
      />

      <Route
        path="/settings/notifications/unsubscribe"
        element={
          <Suspense fallback={<></>}>
            <Unsubscribe />
          </Suspense>
        }
        errorElement={<RouterErrorBoundary />}
      />

      <Route
        path="*"
        element={
          <SlateApp pageProps={pageProps}>
            <Outlet />
          </SlateApp>
        }
        errorElement={<RouterErrorBoundary />}
      >
        <Route
          path="messaging"
          element={
            <Suspense fallback={<></>}>
              <Messaging>
                <Outlet />
              </Messaging>
            </Suspense>
          }
        >
          <Route path="new" element={<MessagingLayout newChat chatOpen />} />
          <Route path=":slug" element={<MessagingLayout chatOpen />} />
          <Route path="*" element={<MessagingLayout />} />
        </Route>

        <Route
          path="oauth/authorize"
          element={
            <Suspense fallback={<></>}>
              <OAuth />
            </Suspense>
          }
        />

        <Route
          path="settings/moderation"
          element={
            <PageLayout layout={SlateLayout.CLASSIC}>
              <Suspense fallback={<></>}>
                <Outlet />
              </Suspense>
            </PageLayout>
          }
        >
          <Route index element={<Moderation />} />
          <Route path=":spaceId/:section?" element={<Moderation />} />
        </Route>

        <Route
          path="settings/*"
          element={
            <SettingsLayout>
              <Suspense fallback={<></>}>
                <Outlet />
              </Suspense>
            </SettingsLayout>
          }
        >
          <Route path="account" element={<MemberAccountSettings />} />
          <Route path="security" element={<MemberSecuritySettings />} />
          <Route
            path="notifications"
            element={<MemberNotificationSettings />}
          />
          <Route path="appearance" element={<MemberColorSchemeSettings />} />
          <Route path="messaging" element={<MemberMessagingSettings />} />
          <Route path="connected-apps" element={<MemberConnectedApps />} />
        </Route>

        <Route
          path="*"
          element={
            <Suspense fallback={<></>}>
              <SpaceRoutes network={network} />
            </Suspense>
          }
        />
      </Route>
    </Routes>
  )
}
